import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Switch } from "../components/ui/switch";
import { CaretLeft, CaretRight, FloppyDisk, CheckCircle, ArrowClockwise, Plus, X, MagnifyingGlass, ArrowSquareOut, Globe } from "@phosphor-icons/react";
import { getSiteConfig, updateSiteConfig, updateUsdRate, getScrapeCategories, putScrapeCategories, listScrapedPages, addFeeSourceUrl, getIngestionConfig, updateIngestionConfig } from "../lib/api";
import { TextureSelect } from "../components/TextureSelect";
import { HIGHLIGHT_PAGES } from "../lib/highlightConfig";
import { toast } from "sonner";

// Darken/lighten a hex colour by `amt` (-1..1) for the gradient's second stop.
function shade(hex, amt) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return hex;
  const adj = (v) => Math.max(0, Math.min(255, Math.round(parseInt(v, 16) * (1 + amt))));
  const h = (n) => n.toString(16).padStart(2, "0");
  return `#${h(adj(m[1]))}${h(adj(m[2]))}${h(adj(m[3]))}`;
}

export default function AdminConfig() {
  const [ph, setPh] = useState(null); // page_highlights object
  const [fee, setFee] = useState({ usd_inr_rate: 83, usd_inr_updated_at: null, show_fees_in_inr: false });
  const [board, setBoard] = useState({ waterfall_colors: ["#2563eb"], offwhite: "#f1f5f9", glaze: true, speed_ms: 400 });
  const [newsTheme, setNewsTheme] = useState("light");
  const [rateLoading, setRateLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cacheBust, setCacheBust] = useState(0);
  const [bumping, setBumping] = useState(false);
  const [bumped, setBumped] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getSiteConfig().then((r) => {
      setPh(r.data.page_highlights || {});
      setFee({
        usd_inr_rate: r.data.usd_inr_rate ?? 83,
        usd_inr_updated_at: r.data.usd_inr_updated_at || null,
        show_fees_in_inr: !!r.data.show_fees_in_inr,
      });
      if (r.data.board_config) setBoard(r.data.board_config);
      setNewsTheme(r.data.news_theme === "dark" ? "dark" : "light");
      setCacheBust(r.data.predictor_cache_bust ?? 0);
    }).catch(() => setPh({}));
  }, []);

  const pageCfg = (pageId) => ph?.[pageId] || { enabled: false, targets: {} };

  const setPageEnabled = (pageId, enabled) =>
    setPh((c) => ({ ...c, [pageId]: { ...pageCfg(pageId), enabled } }));

  const setTargetTexture = (pageId, targetId, texture_id) =>
    setPh((c) => {
      const page = { enabled: false, targets: {}, ...(c?.[pageId] || {}) };
      return {
        ...c,
        [pageId]: { ...page, targets: { ...page.targets, [targetId]: { texture_id } } },
      };
    });

  const fetchRateViaLlm = async () => {
    setRateLoading(true);
    try {
      const r = await updateUsdRate();
      setFee((f) => ({ ...f, usd_inr_rate: r.data.usd_inr_rate, usd_inr_updated_at: r.data.usd_inr_updated_at }));
    } catch (e) { console.error("Rate update failed", e); }
    setRateLoading(false);
  };

  const bumpCache = async () => {
    setBumping(true);
    setBumped(false);
    try {
      const newVal = (Number(cacheBust) || 0) + 1;
      const r = await updateSiteConfig({ predictor_cache_bust: newVal });
      setCacheBust(r.data.predictor_cache_bust ?? newVal);
      setBumped(true);
      setTimeout(() => setBumped(false), 3000);
    } catch (e) { console.error("Cache bump failed", e); }
    setBumping(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const r = await updateSiteConfig({
        page_highlights: ph,
        usd_inr_rate: Number(fee.usd_inr_rate) || 83,
        show_fees_in_inr: !!fee.show_fees_in_inr,
        board_config: board,
        news_theme: newsTheme,
        predictor_cache_bust: cacheBust,
      });
      setPh(r.data.page_highlights || {});
      if (r.data.board_config) setBoard(r.data.board_config);
      if (r.data.news_theme) setNewsTheme(r.data.news_theme);
      setFee({
        usd_inr_rate: r.data.usd_inr_rate ?? 83,
        usd_inr_updated_at: r.data.usd_inr_updated_at || null,
        show_fees_in_inr: !!r.data.show_fees_in_inr,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { console.error("Save failed", e); }
    setSaving(false);
  };

  if (!ph) {
    return <div className="p-10 text-center text-sm text-[#52525B]" data-testid="admin-config-loading">Loading config…</div>;
  }

  return (
    <div className="min-h-screen" data-testid="admin-config-page">
      {/* Header */}
      <div className="border-b border-zinc-200 bg-[#F4F4F5]">
        <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-4 sm:py-6 flex items-end justify-between">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#6043EF] mb-2 hover:underline" data-testid="config-back-link">
              <CaretLeft size={11} weight="bold" /> Back to Admin
            </Link>
            <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold tracking-tight leading-none text-[#09090B] font-heading">
              UI Config
            </h1>
            <p className="text-xs text-[#52525B] mt-1">Apply background textures to paragraphs, per page.</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="h-10 px-5 bg-[#6043EF] text-white font-bold text-xs uppercase tracking-wider hover:bg-[#4A28C2] transition-colors flex items-center gap-2 disabled:opacity-50"
            data-testid="config-save-btn"
          >
            {saved ? <><CheckCircle size={14} weight="bold" /> Saved</> : <><FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}</>}
          </button>
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6 space-y-6">
        <ScrapeCategoriesSection />
        <IngestionConfigSection />
        <CachedFeePagesSection />
        {/* Fees & Currency */}
        <section className="border border-zinc-200" data-testid="config-fees-section">
          <div className="p-4 bg-[#F4F4F5] border-b border-zinc-200">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">Fees & Currency</span>
          </div>
          <div className="p-4 space-y-4 bg-white">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#09090B]">USD → INR exchange rate</p>
                <p className="text-xs text-[#52525B] mt-0.5">
                  Used to normalise USD (NRI) fees for filtering/sorting and INR display. Pulled from a live FX API; auto-refreshes weekly.
                  {fee.usd_inr_updated_at && (
                    <span className="ml-1">Updated {String(fee.usd_inr_updated_at).slice(0, 10)}.</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <input
                  type="number"
                  step="0.01"
                  value={fee.usd_inr_rate}
                  onChange={(e) => setFee((f) => ({ ...f, usd_inr_rate: e.target.value }))}
                  className="h-9 w-24 border border-zinc-300 px-2 font-['IBM_Plex_Mono'] text-sm text-right"
                  data-testid="config-usd-rate-input"
                />
                <button
                  type="button"
                  onClick={fetchRateViaLlm}
                  disabled={rateLoading}
                  className="h-9 px-3 bg-[#09090B] text-white font-bold text-[10px] uppercase tracking-wider hover:bg-zinc-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="config-usd-rate-llm-btn"
                >
                  <ArrowClockwise size={12} weight="bold" className={rateLoading ? "animate-spin" : ""} />
                  {rateLoading ? "Fetching…" : "Fetch live rate"}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between gap-4 border-t border-zinc-100 pt-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#09090B]">Show USD fees in INR by default</p>
                <p className="text-xs text-[#52525B] mt-0.5">Site-wide default for the ₹/$ toggle shown next to USD fees.</p>
              </div>
              <Switch
                checked={!!fee.show_fees_in_inr}
                onCheckedChange={(v) => setFee((f) => ({ ...f, show_fees_in_inr: v }))}
                data-testid="config-show-inr-toggle"
              />
            </div>
          </div>
        </section>

        {/* Predictor cache — force returning users to refetch after data edits */}
        <section className="border border-zinc-200" data-testid="config-predictor-cache-section">
          <div className="p-4 bg-[#F4F4F5] border-b border-zinc-200">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">Predictor Cache</span>
          </div>
          <div className="p-4 bg-white flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#09090B]">Force refresh predictor results</p>
              <p className="text-xs text-[#52525B] mt-0.5">
                Returning visitors keep their last predictor results cached in their browser. After surgical data edits
                (cutoffs, categories, merges), bump this to make every user drop their cached results and refetch fresh data.
                Current version: <span className="font-['IBM_Plex_Mono'] font-bold text-[#09090B]">v{cacheBust}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={bumpCache}
              disabled={bumping}
              className="h-9 px-4 bg-[#6043EF] text-white font-bold text-[10px] uppercase tracking-wider hover:bg-[#4A28C2] transition-colors flex items-center gap-1.5 disabled:opacity-50 shrink-0"
              data-testid="config-bump-cache-btn"
            >
              {bumped ? <><CheckCircle size={12} weight="bold" /> Bumped → v{cacheBust}</> : <><ArrowClockwise size={12} weight="bold" className={bumping ? "animate-spin" : ""} /> {bumping ? "Bumping…" : "Bump cache version"}</>}
            </button>
          </div>
        </section>

        <section className="border border-zinc-200" data-testid="config-board-section">
          <div className="p-4 bg-[#F4F4F5] border-b border-zinc-200 flex items-center justify-between gap-4 flex-wrap">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">Counselling Board — Live Row Colours</span>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-zinc-600 cursor-pointer" data-testid="board-hide-demo-toggle">
                <input type="checkbox" checked={!!board.hide_demo} onChange={(e) => setBoard((b) => ({ ...b, hide_demo: e.target.checked }))} className="w-4 h-4 accent-[#6043EF]" />
                Hide demo boards {board.hide_demo ? "ON" : "OFF"}
              </label>
              <label className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-zinc-600 cursor-pointer" data-testid="board-glaze-toggle">
                <input type="checkbox" checked={!!board.glaze} onChange={(e) => setBoard((b) => ({ ...b, glaze: e.target.checked }))} className="w-4 h-4 accent-[#6043EF]" />
                Running glaze {board.glaze ? "ON" : "OFF"}
              </label>
            </div>
          </div>
          <div className="p-4 space-y-4">
            <p className="text-[11px] text-zinc-500">A moving block of these colours slides down the in-progress rows (wrapping continuously); every other live row uses the off-white background below. One colour = a single band sliding down.</p>
            {/* palette chips */}
            <div className="flex flex-wrap items-center gap-2" data-testid="board-palette">
              {(board.waterfall_colors || []).map((c, i) => (
                <div key={i} className="relative flex items-center gap-1 rounded-lg border border-zinc-200 p-1 pr-2" data-testid={`palette-color-${i}`}>
                  <input type="color" value={c} onChange={(e) => setBoard((b) => { const w = [...b.waterfall_colors]; w[i] = e.target.value; return { ...b, waterfall_colors: w }; })}
                    className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent" style={{ padding: 0 }} data-testid={`palette-input-${i}`} />
                  <span className="font-mono text-[10px] text-zinc-500">{c}</span>
                  {board.waterfall_colors.length > 1 && (
                    <button onClick={() => setBoard((b) => ({ ...b, waterfall_colors: b.waterfall_colors.filter((_, j) => j !== i) }))}
                      className="ml-1 text-zinc-400 hover:text-rose-600" data-testid={`palette-remove-${i}`} title="Remove">
                      <X size={12} weight="bold" />
                    </button>
                  )}
                </div>
              ))}
              {board.waterfall_colors.length < 8 && (
                <button onClick={() => setBoard((b) => ({ ...b, waterfall_colors: [...b.waterfall_colors, "#3b82f6"] }))}
                  data-testid="palette-add-btn"
                  className="w-10 h-10 rounded-lg border-2 border-dashed border-zinc-300 text-zinc-400 hover:border-[#6043EF] hover:text-[#6043EF] flex items-center justify-center transition-colors">
                  <Plus size={16} weight="bold" />
                </button>
              )}
            </div>
            {/* off-white background for non-window rows */}
            <div className="flex items-center gap-2" data-testid="board-offwhite">
              <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-600">Off-white background</span>
              <div className="relative flex items-center gap-1 rounded-lg border border-zinc-200 p-1 pr-2">
                <input type="color" value={board.offwhite || "#f1f5f9"} onChange={(e) => setBoard((b) => ({ ...b, offwhite: e.target.value }))}
                  className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent" style={{ padding: 0 }} data-testid="offwhite-input" />
                <span className="font-mono text-[10px] text-zinc-500">{board.offwhite || "#f1f5f9"}</span>
              </div>
            </div>
            {/* speed */}
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-600">Cascade speed</span>
              <input type="range" min="150" max="1500" step="50" value={board.speed_ms}
                onChange={(e) => setBoard((b) => ({ ...b, speed_ms: parseInt(e.target.value) }))}
                className="flex-1 max-w-[240px] accent-[#6043EF]" data-testid="board-speed" />
              <span className="font-mono text-[11px] text-zinc-500 tabular-nums">{board.speed_ms}ms / step</span>
            </div>
            {/* live preview */}
            <div className="rounded-lg overflow-hidden border border-zinc-200" data-testid="board-preview">
              {board.waterfall_colors.map((c, i) => (
                <div key={i} className="relative overflow-hidden h-8 flex items-center px-3 text-[11px] font-bold text-white"
                  style={{ background: `linear-gradient(100deg, ${c}, ${shade(c, -0.18)})` }}>
                  Ongoing · in window
                  {board.glaze && <span aria-hidden className="absolute inset-y-0 -left-1/3 w-1/3 skew-x-[-18deg] bg-white/25" style={{ animation: "rowShine 3s linear infinite" }} />}
                </div>
              ))}
              {[0, 1].map((i) => (
                <div key={`ow-${i}`} className="h-8 flex items-center px-3 text-[11px] font-bold text-[#09090B]"
                  style={{ background: `linear-gradient(100deg, ${board.offwhite || "#f1f5f9"}, ${shade(board.offwhite || "#f1f5f9", -0.05)})` }}>
                  Ongoing · off-white
                </div>
              ))}
            </div>
          </div>
        </section>
        <style>{`@keyframes rowShine{0%{transform:translateX(-60%) skewX(-18deg)}100%{transform:translateX(560%) skewX(-18deg)}}`}</style>

        {/* Colleges News Panel — theme */}
        <section className="border border-zinc-200" data-testid="config-news-theme-section">
          <div className="p-4 bg-[#F4F4F5] border-b border-zinc-200">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">Colleges News Panel — Theme</span>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-[11px] text-zinc-500">The TV-style News &amp; Events panel on the Colleges page. Light is the default; Dark keeps the cinematic look.</p>
            <div className="inline-flex rounded-lg border border-zinc-200 overflow-hidden" data-testid="news-theme-toggle">
              {["light", "dark"].map((t) => (
                <button key={t} onClick={() => setNewsTheme(t)} data-testid={`news-theme-${t}`}
                  className={`h-9 px-5 text-xs font-bold uppercase tracking-wider transition-colors ${newsTheme === t ? "bg-[#6043EF] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
                  {t}
                </button>
              ))}
            </div>
            {/* mini preview */}
            <div className="rounded-xl p-2 max-w-md" style={{ background: newsTheme === "dark" ? "linear-gradient(180deg,#3f3f46,#0a0a0a 60%)" : "linear-gradient(180deg,#e4e4e7,#f4f4f5 60%)" }}>
              <div className="rounded-lg p-3" style={{ background: newsTheme === "dark" ? "radial-gradient(120% 100% at 50% -10%, #14284d 0%, #060913 100%)" : "radial-gradient(120% 100% at 50% -10%, #eff6ff 0%, #ffffff 100%)" }}>
                <div className={`text-[11px] font-black ${newsTheme === "dark" ? "text-white" : "text-[#09090B]"}`}>NEET<span className="text-sky-500">NEWS</span> 24×7</div>
                <div className={`mt-2 rounded-md border p-2 ${newsTheme === "dark" ? "bg-white/[0.06] border-white/10" : "bg-white border-zinc-200"}`}>
                  <span className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-600 text-white">Result</span>
                  <div className={`text-[11px] font-bold mt-1 ${newsTheme === "dark" ? "text-white" : "text-[#09090B]"}`}>Round 1 seat allotment declared</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {HIGHLIGHT_PAGES.map((page) => {
          const cfg = pageCfg(page.id);
          return (
            <section key={page.id} className="border border-zinc-200" data-testid={`config-page-${page.id}`}>
              <div className="p-4 bg-[#F4F4F5] border-b border-zinc-200 flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">{page.label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-[#52525B]">Apply</span>
                  <Switch
                    checked={!!cfg.enabled}
                    onCheckedChange={(v) => setPageEnabled(page.id, v)}
                    data-testid={`config-toggle-${page.id}`}
                  />
                </div>
              </div>

              <div className={`${!cfg.enabled ? "opacity-50 pointer-events-none" : ""}`}>
                {["text", "card"].map((kind) => {
                  const items = page.targets.filter((t) => t.kind === kind);
                  if (items.length === 0) return null;
                  return (
                    <div key={kind}>
                      <div className="px-4 pt-3 pb-1 bg-white">
                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6043EF]">
                          {kind === "text" ? "Paragraphs" : "Cards"}
                        </span>
                      </div>
                      <div className="divide-y divide-zinc-100">
                        {items.map((t) => (
                          <div key={t.id} className="p-4 flex items-center justify-between gap-4" data-testid={`config-target-${page.id}-${t.id}`}>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-[#09090B]">{t.label}</p>
                              {t.sample && <p className="text-xs text-[#52525B] mt-0.5 truncate max-w-md">{t.sample}</p>}
                            </div>
                            <TextureSelect
                              value={cfg.targets?.[t.id]?.texture_id || ""}
                              onChange={(id) => setTargetTexture(page.id, t.id, id)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}


function ScrapeCategoriesSection() {
  const [cats, setCats] = useState(null); // [{name, hint, cache}]
  const [defaults, setDefaults] = useState([]);
  const [ttl, setTtl] = useState("1w");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getScrapeCategories()
      .then((r) => { setCats(r.data.categories || []); setDefaults(r.data.defaults || []); setTtl(r.data.ttl || "1w"); })
      .catch(() => setCats([]));
  }, []);

  const setField = (i, key, val) => setCats((c) => c.map((x, idx) => idx === i ? { ...x, [key]: val } : x));
  const addRow = () => setCats((c) => [...c, { name: "", hint: "", cache: false }]);
  const removeRow = (i) => setCats((c) => c.filter((_, idx) => idx !== i));
  const resetDefaults = () => setCats(defaults.map((d) => ({ ...d })));

  const save = async () => {
    const cleaned = (cats || [])
      .map((c) => ({ name: (c.name || "").trim(), hint: (c.hint || "").trim(), cache: !!c.cache }))
      .filter((c) => c.name && c.hint);
    if (!cleaned.length) { toast.error("Add at least one category with a name and hint"); return; }
    setSaving(true);
    try {
      const { data } = await putScrapeCategories(cleaned, ttl);
      setCats(data.categories || cleaned);
      setTtl(data.ttl || ttl);
      toast.success("Scrape categories saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save categories");
    } finally {
      setSaving(false);
    }
  };

  const TTL_LABEL = { "1d": "1 Day", "1w": "1 Week", "1m": "1 Month" };

  return (
    <section className="border border-zinc-200" data-testid="config-scrape-categories-section">
      <div className="p-4 bg-[#F4F4F5] border-b border-zinc-200 flex items-center justify-between gap-3">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">Scraper Page-Tree Categories</span>
          <p className="text-[11px] text-[#52525B] mt-1">Which sub-pages the official-site scraper discovers &amp; fetches. Each category matches a link's text/URL by a case-insensitive regex "hint" (use <code className="font-['IBM_Plex_Mono']">|</code> for alternatives). Turn on <b>Cache</b> to store a category's fetched page text and reuse it until older than the TTL (avoids re-scraping). <b>news</b> is treated specially (seeded to news).</p>
        </div>
        <button onClick={resetDefaults} className="shrink-0 h-8 px-3 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 text-[#52525B] rounded-md hover:border-[#6043EF] hover:text-[#6043EF] transition-colors" data-testid="scrape-cat-reset">Reset defaults</button>
      </div>
      <div className="p-4 bg-white space-y-2">
        {cats === null ? (
          <div className="text-xs text-zinc-400">Loading…</div>
        ) : (
          <>
            <div className="flex items-center gap-3 pb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#52525B]">Cache TTL</span>
              <div className="flex rounded-md border border-zinc-200 overflow-hidden" data-testid="scrape-ttl-toggle">
                {["1d", "1w", "1m"].map((t) => (
                  <button key={t} type="button" onClick={() => setTtl(t)}
                    className={`px-3 h-8 text-[11px] font-bold ${ttl === t ? "bg-[#6043EF] text-white" : "bg-white text-zinc-500 hover:bg-zinc-50"}`}
                    data-testid={`scrape-ttl-${t}`}>{TTL_LABEL[t]}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-[150px_1fr_70px_32px] gap-2 text-[9px] font-bold uppercase tracking-wider text-zinc-400 px-1">
              <span>Name</span><span>Hint (regex)</span><span className="text-center">Cache</span><span />
            </div>
            {cats.map((c, i) => (
              <div key={i} className="grid grid-cols-[150px_1fr_70px_32px] gap-2 items-center" data-testid={`scrape-cat-row-${i}`}>
                <input value={c.name} onChange={(e) => setField(i, "name", e.target.value)} placeholder="fees"
                  className="h-9 border border-zinc-300 px-2 text-sm font-['IBM_Plex_Mono'] rounded-md" data-testid={`scrape-cat-name-${i}`} />
                <input value={c.hint} onChange={(e) => setField(i, "hint", e.target.value)} placeholder="fee|tuition|charges"
                  className="h-9 border border-zinc-300 px-2 text-sm font-['IBM_Plex_Mono'] rounded-md" data-testid={`scrape-cat-hint-${i}`} />
                <div className="flex justify-center">
                  <Switch checked={!!c.cache} onCheckedChange={(v) => setField(i, "cache", v)} data-testid={`scrape-cat-cache-${i}`} />
                </div>
                <button onClick={() => removeRow(i)} className="h-9 w-8 flex items-center justify-center text-rose-500 hover:text-rose-700" title="Remove" data-testid={`scrape-cat-remove-${i}`}>
                  <X size={14} weight="bold" />
                </button>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2">
              <button onClick={addRow} className="inline-flex items-center gap-1 h-8 px-3 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 text-[#6043EF] rounded-md hover:bg-[#6043EF]/5 transition-colors" data-testid="scrape-cat-add">
                <Plus size={12} weight="bold" /> Add category
              </button>
              <button onClick={save} disabled={saving} className="h-9 px-5 bg-[#6043EF] text-white font-bold text-xs uppercase tracking-wider rounded-md hover:bg-[#4A28C2] transition-colors flex items-center gap-2 disabled:opacity-50" data-testid="scrape-cat-save">
                <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save Categories"}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function IngestionConfigSection() {
  const [cfg, setCfg] = useState(null); // {mcc_batch_size, default, min, max}
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    getIngestionConfig().then(({ data }) => setCfg(data))
      .catch(() => setCfg({ mcc_batch_size: 25, default: 25, min: 5, max: 100 }));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await updateIngestionConfig({
        mcc_batch_size: parseInt(cfg.mcc_batch_size, 10),
        mcc_final_match_enabled: !!cfg.mcc_final_match_enabled,
        mcc_final_match_chunk: parseInt(cfg.mcc_final_match_chunk, 10) || 1,
      });
      setCfg((c) => ({ ...c, ...data }));
      toast.success(`Ingestion config saved · batch ${data.mcc_batch_size ?? cfg.mcc_batch_size} · final match ${data.mcc_final_match_enabled ? "on" : "off"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };
  return (
    <section className="border border-zinc-200" data-testid="config-ingestion-section">
      <div className="p-4 bg-[#F4F4F5] border-b border-zinc-200">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">Ingestion Tuning</span>
        <p className="text-[11px] text-[#52525B] mt-1">Pages parsed per batch for <b>all</b> subprocess-batched allotment ingests (MCC, Karnataka, … — every parser routed through the shared chunked pipeline). <b>Lower</b> keeps the app responsive on production — the parse returns control more often, so the health probe &amp; progress heartbeat don't miss, avoiding the pod being restarted mid-parse (which cancels the ingest). Applies to the <b>next</b> ingest. Default {cfg?.default ?? 25}.</p>
      </div>
      <div className="p-4 bg-white">
        {cfg === null ? <div className="text-xs text-zinc-400">Loading…</div> : (
          <div className="space-y-4">
            <div className="flex items-end gap-4 flex-wrap">
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#52525B]">Pages / batch (all parsers)</span>
                <input type="number" min={cfg.min} max={cfg.max} value={cfg.mcc_batch_size}
                  onChange={(e) => setCfg((c) => ({ ...c, mcc_batch_size: e.target.value }))}
                  className="mt-1 h-9 w-28 border border-zinc-300 px-2 text-sm rounded-md font-['IBM_Plex_Mono']" data-testid="ingestion-batch-size-input" />
              </label>
              <div className="flex gap-1.5">
                {[10, 25, 50].map((v) => (
                  <button key={v} type="button" onClick={() => setCfg((c) => ({ ...c, mcc_batch_size: v }))}
                    className={`h-9 px-3 text-[11px] font-bold rounded-md border ${String(cfg.mcc_batch_size) === String(v) ? "bg-[#6043EF] text-white border-[#6043EF]" : "border-zinc-300 text-zinc-500 hover:bg-zinc-50"}`}
                    data-testid={`ingestion-batch-preset-${v}`}>{v}</button>
                ))}
              </div>
              <span className="text-[10px] text-zinc-400 max-w-[220px]">Range {cfg.min}–{cfg.max}. Smaller = safer on prod, slightly slower overall.</span>
            </div>

            <div className="border-t border-zinc-100 pt-4 flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Switch checked={!!cfg.mcc_final_match_enabled}
                  onCheckedChange={(v) => setCfg((c) => ({ ...c, mcc_final_match_enabled: v }))}
                  data-testid="ingestion-final-match-toggle" />
                <span className="text-[11px] font-bold text-[#09090B]">Final college-match leg (fuzzy)</span>
              </div>
              <label className={`block ${cfg.mcc_final_match_enabled ? "" : "opacity-40 pointer-events-none"}`}>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#52525B]">Names / yield</span>
                <input type="number" min={1} max={50} value={cfg.mcc_final_match_chunk}
                  onChange={(e) => setCfg((c) => ({ ...c, mcc_final_match_chunk: e.target.value }))}
                  className="mt-1 h-9 w-24 border border-zinc-300 px-2 text-sm rounded-md font-['IBM_Plex_Mono']" data-testid="ingestion-final-chunk-input" />
              </label>
              <span className="text-[10px] text-zinc-400 max-w-[320px]">Turn OFF to finish the ingest right after parsing and resolve all unmatched via the per-state LLM / manual assign (recommended if this leg keeps getting cancelled). Smaller "names / yield" = safer on prod.</span>
            </div>

            <div className="flex">
              <button onClick={save} disabled={saving} className="ml-auto h-9 px-5 bg-[#6043EF] text-white font-bold text-xs uppercase tracking-wider rounded-md hover:bg-[#4A28C2] transition-colors flex items-center gap-2 disabled:opacity-50" data-testid="ingestion-config-save">
                <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function CachedFeePagesSection() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 5;

  const load = (pg = 0, query = q) => {
    setLoading(true);
    listScrapedPages(query, pg * limit, limit)
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); setPage(pg); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(0, ""); }, []); // eslint-disable-line
  // debounce search
  useEffect(() => {
    const t = setTimeout(() => load(0, q), 350);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line

  const pages = Math.ceil(total / limit);
  return (
    <section className="border border-zinc-200" data-testid="config-cached-pages-section">
      <div className="p-4 bg-[#F4F4F5] border-b border-zinc-200">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B] flex items-center gap-1.5">
          <Globe size={14} weight="bold" className="text-[#0369A1]" /> Cached Fee Pages
        </span>
        <p className="text-[11px] text-[#52525B] mt-1">Pages the scraper has cached for fee lookups. Search a college and open any stored page directly.</p>
      </div>
      <div className="p-4 bg-white space-y-3">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search college by name, code or state…"
            className="w-full h-9 border border-zinc-300 rounded-md pl-8 pr-3 text-sm" data-testid="cached-pages-search" />
        </div>
        {loading ? (
          <div className="text-xs text-zinc-400 py-4">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-xs text-zinc-400 py-4" data-testid="cached-pages-empty">No cached pages{q ? " for this search" : " yet"}.</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {items.map((it) => (
              <CachedCollegeRow key={it.college_code} it={it} onAdded={() => load(page)} />
            ))}
          </div>
        )}
        {pages > 1 && (
          <div className="flex items-center justify-between pt-1">
            <span className="text-[10px] text-zinc-400">{total} colleges · page {page + 1}/{pages}</span>
            <div className="flex items-center gap-1">
              <button disabled={page === 0} onClick={() => load(page - 1)} className="h-7 w-7 flex items-center justify-center border border-zinc-300 rounded-md disabled:opacity-40 hover:border-[#6043EF]" data-testid="cached-pages-prev"><CaretLeft size={13} /></button>
              <button disabled={page >= pages - 1} onClick={() => load(page + 1)} className="h-7 w-7 flex items-center justify-center border border-zinc-300 rounded-md disabled:opacity-40 hover:border-[#6043EF]" data-testid="cached-pages-next"><CaretRight size={13} /></button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}



const CAT_CLS = { home: "bg-zinc-100 text-zinc-600", fees: "bg-emerald-100 text-emerald-700", admission: "bg-sky-100 text-sky-700", prospectus: "bg-amber-100 text-amber-700" };

function CachedCollegeRow({ it, onAdded }) {
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const u = url.trim();
    if (!u) return;
    setBusy(true);
    try {
      const { data } = await addFeeSourceUrl(it.college_code, u);
      if (data?.ok) {
        toast.success("Fee page fetched & cached");
        setUrl(""); setAdding(false);
        onAdded?.();
      } else {
        toast.error(data?.message || "Could not fetch that URL");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add that URL");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="py-2.5" data-testid={`cached-pages-college-${it.college_code}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-bold text-[#09090B]">{it.name || it.college_code}</span>
        <span className="text-[10px] font-['IBM_Plex_Mono'] text-zinc-400">{it.college_code}</span>
        {it.state && <span className="text-[10px] text-zinc-400">· {it.state}</span>}
        <span className="ml-auto text-[10px] text-zinc-400">{it.pages?.length || 0} page(s)</span>
      </div>
      <ul className="space-y-0.5 pl-1">
        {(it.pages || []).map((p, k) => (
          <li key={k} className="flex items-center gap-2 text-[11px]">
            <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider ${CAT_CLS[p.category] || "bg-zinc-100 text-zinc-500"}`}>{p.category || "?"}</span>
            <a href={p.url} target="_blank" rel="noreferrer" className="text-[#0369A1] hover:underline inline-flex items-center gap-1 truncate max-w-[70%]" title={p.url}>
              <ArrowSquareOut size={10} /> {p.url}
            </a>
            {p.fetched_at && <span className="text-[10px] text-zinc-400 ml-auto shrink-0">{new Date(p.fetched_at).toLocaleDateString()}</span>}
          </li>
        ))}
      </ul>
      {adding ? (
        <div className="flex items-center gap-2 pl-1 mt-1.5">
          <input autoFocus value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://college.edu/fees"
            onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") { setAdding(false); setUrl(""); } }}
            className="flex-1 h-8 border border-zinc-300 rounded-md px-2 text-[11px] font-['IBM_Plex_Mono']" data-testid={`cached-add-input-${it.college_code}`} />
          <button onClick={submit} disabled={busy || !url.trim()} className="h-8 px-3 bg-[#0369A1] text-white text-[10px] font-bold uppercase tracking-wider rounded-md hover:bg-[#025585] disabled:opacity-50" data-testid={`cached-add-submit-${it.college_code}`}>
            {busy ? "…" : "Fetch"}
          </button>
          <button onClick={() => { setAdding(false); setUrl(""); }} className="h-8 w-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700"><X size={13} /></button>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1 mt-1.5 ml-1 h-6 px-2 text-[10px] font-bold uppercase tracking-wider text-[#0369A1] border border-dashed border-[#0369A1]/40 rounded-md hover:bg-[#0369A1]/5" data-testid={`cached-add-btn-${it.college_code}`}>
          <Plus size={11} weight="bold" /> Add fee source
        </button>
      )}
    </div>
  );
}
